import { useEffect, useState } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, Bookmark, CalendarDays, Clock3, Link2, Linkedin, MessageCircle, Twitter } from "lucide-react";
import { toast } from "sonner";
import { articles, getArticle, isSaved, relatedArticles, toggleSave } from "@/data/journal";
import ArticleCard from "@/components/ArticleCard";

const Block = ({ block }) => {
  if (block.t === "h") {
    if (block.level === "h2") return <h2>{block.text}</h2>;
    if (block.level === "h3") return <h3>{block.text}</h3>;
    return <h4>{block.text}</h4>;
  }
  if (block.t === "p") return <p dangerouslySetInnerHTML={{ __html: block.html }} />;
  if (block.t === "list") {
    const items = block.items.map((item, i) => <li key={i} dangerouslySetInnerHTML={{ __html: item }} />);
    return block.ordered ? <ol>{items}</ol> : <ul>{items}</ul>;
  }
  if (block.t === "quote") return <blockquote>{block.text}</blockquote>;
  if (block.t === "img") return <figure><img src={block.src} alt={block.alt || ""} loading="lazy" /></figure>;
  return null;
};

export default function BlogArticle() {
  const { slug } = useParams();
  const article = getArticle(slug);
  const [saved, setSaved] = useState(() => isSaved(slug));

  useEffect(() => { setSaved(isSaved(slug)); }, [slug]);
  if (!article) return <Navigate to="/blogs" replace />;

  const index = articles.findIndex(a => a.slug === slug);
  const prev = articles[index - 1];
  const next = articles[index + 1];
  const related = relatedArticles(article);
  const pageUrl = window.location.href;
  const shareText = encodeURIComponent(`${article.title} — ADVOKIT Journal`);
  const shareUrl = encodeURIComponent(pageUrl);

  const onSave = () => {
    const nowSaved = toggleSave(slug).includes(slug);
    setSaved(nowSaved);
    toast.success(nowSaved ? "Article saved to your reading list" : "Removed from your saved articles");
  };
  const onCopy = () => {
    navigator.clipboard.writeText(pageUrl).then(() => toast.success("Article link copied"));
  };

  return (
    <main data-testid="article-page">
      <article className="article-hero">
        <Link to="/blogs" className="back-link" data-testid="back-to-journal-link"><ArrowLeft size={13} /> Back to the journal</Link>
        <div className="journal-meta article-cat"><span className="journal-cat">{article.category}</span>{article.featured && <span className="featured-tag">Featured</span>}</div>
        <h1 data-testid="article-title">{article.title}</h1>
        <div className="article-meta-row">
          {article.date && <span data-testid="article-date"><CalendarDays size={13} /> {article.date}</span>}
          <span data-testid="article-reading-time"><Clock3 size={13} /> {article.readingTime} min read</span>
          <span>ADVOKIT Journal</span>
        </div>
        <div className="article-actions">
          <button className={`action-btn ${saved ? "saved" : ""}`} data-testid="save-article-button" onClick={onSave}>
            <Bookmark size={14} fill={saved ? "currentColor" : "none"} /> {saved ? "Saved" : "Save article"}
          </button>
          <a className="action-btn" data-testid="share-whatsapp-button" href={`https://wa.me/?text=${shareText}%20${shareUrl}`} target="_blank" rel="noreferrer"><MessageCircle size={14} /> WhatsApp</a>
          <a className="action-btn" data-testid="share-x-button" href={`https://twitter.com/intent/tweet?text=${shareText}&url=${shareUrl}`} target="_blank" rel="noreferrer"><Twitter size={14} /> Post on X</a>
          <a className="action-btn" data-testid="share-linkedin-button" href={`https://www.linkedin.com/sharing/share-offsite/?url=${shareUrl}`} target="_blank" rel="noreferrer"><Linkedin size={14} /> LinkedIn</a>
          <button className="action-btn" data-testid="share-copy-button" onClick={onCopy}><Link2 size={14} /> Copy link</button>
        </div>
      </article>

      <div className="article-cover"><img src={article.image} alt={article.title} /></div>

      <div className="article-body" data-testid="article-content">
        {article.blocks.map((block, i) => <Block key={i} block={block} />)}
      </div>

      <nav className="article-nav">
        {prev ? <Link to={`/blogs/${prev.slug}`} data-testid="prev-article-link"><ArrowLeft size={14} /> Previous<span>{prev.title}</span></Link> : <span />}
        {next ? <Link to={`/blogs/${next.slug}`} className="next" data-testid="next-article-link">Next <ArrowRight size={14} /><span>{next.title}</span></Link> : <span />}
      </nav>

      <section className="article-cta" data-testid="article-mentor-cta">
        <div className="article-cta-inner">
          <div className="eyebrow gold"><span />Discuss this topic</div>
          <h2>Questions about {article.category.toLowerCase()} in real practice?</h2>
          <p>Ask an ADVOKIT mentor how topics like this play out in chambers and courtrooms — and how our programmes train you to handle them.</p>
          <a className="whatsapp-btn" data-testid="article-mentor-whatsapp-button" href={`https://wa.me/917309606226?text=${encodeURIComponent(`Hello ADVOKIT, I just read "${article.title}" on your journal and would like to discuss this topic with a mentor.`)}`} target="_blank" rel="noreferrer">Ask a mentor about this topic <MessageCircle size={17} /></a>
        </div>
      </section>

      {related.length > 0 && (
        <section className="related-section">
          <div className="eyebrow"><span />Keep reading</div>
          <h2>More on {article.category}</h2>
          <div className="journal-grid">
            {related.map((a, i) => <ArticleCard key={a.slug} article={a} index={`related-${i + 1}`} />)}
          </div>
        </section>
      )}
    </main>
  );
}
